import React, { useState } from "react";
import { Link, Navigate } from "@/lib/router-compat";
import { ArrowLeft, Phone } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { LinkedText } from "../components/LinkedText";
import { QuoteModal } from "../components/QuoteModal";
import { PHONE_DISPLAY, PHONE_LINK } from "../data/business";
import { getPost } from "../data/posts";

const BlogPostPage: React.FC<{ slug: string }> = ({ slug }) => {
  const post = getPost(slug);
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="min-h-screen flex flex-col bg-[#F8F6F1] text-[#252A2B] overflow-x-hidden">
      <Seo
        title={post.seoTitle}
        description={post.metaDescription}
        canonicalPath={`/blog/${post.slug}`}
      />
      <Header />
      <main className="flex-grow">
        <section className="bg-[#252A2B] text-white">
          <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 md:py-14 lg:px-8">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-[#D96B27]"
            >
              <ArrowLeft className="h-4 w-4" />
              Blog & Our Stories
            </Link>
            <p className="mt-6 text-xs font-bold uppercase tracking-[0.16em] text-[#D96B27]">
              {post.dateLabel}
            </p>
            <h1 className="mt-3 font-display text-3xl font-bold uppercase leading-tight sm:text-4xl lg:text-5xl">
              {post.title}
            </h1>
            <img
              src={post.image}
              alt={post.imageAlt}
              className="mt-8 w-full rounded-2xl object-cover"
            />
          </div>
        </section>
        <article className="py-10 md:py-14">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="space-y-10">
              {post.sections.map((section) => (
                <section key={section.heading}>
                  <h2 className="font-display text-2xl font-bold uppercase leading-tight">
                    {section.heading}
                  </h2>
                  <div className="mt-4 space-y-4 text-sm leading-relaxed text-[#252A2B]/85 sm:text-base">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph}>
                        <LinkedText text={paragraph} />
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </article>
        <section className="pb-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl bg-[#252A2B] p-6 text-white sm:p-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D96B27]">
                Free estimate
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold uppercase leading-tight">
                Ready to book the lot?
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-white/80 sm:text-base">
                Call the team or request a free quote for fall cleanup or snow
                removal in Highland.
              </p>
              <div className="mt-6 flex flex-wrap items-start gap-3">
                <div className="flex flex-col items-center">
                  <a
                    href={PHONE_LINK}
                    className="inline-flex items-center gap-2 rounded-full border-2 border-[#D96B27] bg-[#D96B27] px-6 py-3 text-sm font-bold uppercase tracking-wide text-white shadow-md transition-all hover:border-[#BA541B] hover:bg-[#BA541B]"
                  >
                    <Phone className="h-4 w-4" />
                    {PHONE_DISPLAY}
                  </a>
                  <span className="mt-1 text-[10px] font-bold uppercase tracking-[0.15em] text-white/80">
                    We text 24/7
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsQuoteOpen(true)}
                  className="inline-flex items-center rounded-full border-2 border-white bg-white px-6 py-3 text-sm font-bold uppercase tracking-wide text-[#252A2B] shadow-md transition-all hover:bg-white/90"
                >
                  Get Free Quote
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <QuoteModal
        isOpen={isQuoteOpen}
        onClose={() => setIsQuoteOpen(false)}
      />
    </div>
  );
};

export default BlogPostPage;
